import { getItem } from '../data/items.js';
import { getQuest, MAX_ACTIVE_QUESTS, QUEST_NPC_DEFAULT_ZONE, QUESTS, } from '../data/quests.js';
import { carriedEntries, wornPieces } from './inventory.js';
import { killXp } from './progression.js';
/** The camp's merchant is not Havrebourg's (ADR 0066): a townsperson answers in their own zone only. */
export function isQuestNpc(ref, npc) {
    return (ref.mob === npc.mob &&
        ref.biome === npc.biome &&
        (ref.zone ?? QUEST_NPC_DEFAULT_ZONE) === (npc.zone ?? QUEST_NPC_DEFAULT_ZONE));
}
/**
 * The counters a quest's kills and camps are measured from, only those it
 * asks. `openCamps` are the regions where the character opened a camp's
 * chest whose keepers are not back yet: that camp counts for the quest taken
 * now, for a player sent to hunt the region's goblins clears their camp
 * before being asked to.
 */
export function baselineOf(quest, career, openCamps = new Set()) {
    const baseline = { kills: {}, camps: {} };
    for (const objective of quest.objectives) {
        if (objective.kind === 'kill') {
            for (const mob of objective.mobs) {
                baseline.kills[mob] = career.kills[mob] ?? 0;
                baseline.lent = { ...baseline.lent, [mob]: career.lentKills[mob] ?? 0 };
            }
        }
        else if (objective.kind === 'camp') {
            const camps = career.camps[objective.biome] ?? 0;
            baseline.camps[objective.biome] = openCamps.has(objective.biome)
                ? Math.max(0, camps - 1)
                : camps;
        }
        else if (objective.kind === 'raid') {
            baseline.shefiraKills = career.shefiraKills;
        }
    }
    return baseline;
}
/** How many of a template the bag holds, every stack together, the wardrobe's too (ADR 0072). */
export function bagCount(inventory, templateId) {
    let total = 0;
    for (const entry of carriedEntries(inventory)) {
        if (entry.item.templateId === templateId)
            total += entry.item.quantity;
    }
    return total;
}
/**
 * The highest smith's level among the pieces worn, cosmetics aside (ADR
 * 0031); with `templateIds`, among those pieces only.
 */
export function bestWornUpgrade(inventory, templateIds) {
    let best = 0;
    for (const piece of wornPieces(inventory.equipped, getItem)) {
        if (templateIds !== undefined && !templateIds.includes(piece.template.id))
            continue;
        best = Math.max(best, piece.upgradeLevel);
    }
    return best;
}
/** Whether one of these templates is worn, cosmetics aside (ADR 0031). */
export function wearsOneOf(inventory, templateIds) {
    return wornPieces(inventory.equipped, getItem).some((piece) => templateIds.includes(piece.template.id));
}
export function objectiveProgress(objective, index, quest, facts) {
    const clamp = (value, target) => ({
        value: Math.max(0, Math.min(target, value)),
        target,
    });
    switch (objective.kind) {
        case 'kill': {
            let killed = 0;
            for (const mob of objective.mobs) {
                // A kill lent by a party member who did not hunt the mob does not count (ADR 0047).
                const lent = (facts.career.lentKills[mob] ?? 0) - (quest.baseline.lent?.[mob] ?? 0);
                killed += (facts.career.kills[mob] ?? 0) - (quest.baseline.kills[mob] ?? 0) - lent;
            }
            return clamp(killed, objective.count);
        }
        case 'camp':
            return clamp((facts.career.camps[objective.biome] ?? 0) - (quest.baseline.camps[objective.biome] ?? 0), objective.count);
        case 'raid':
            return clamp(facts.career.shefiraKills - (quest.baseline.shefiraKills ?? 0), objective.count);
        case 'collect':
            return clamp(bagCount(facts.inventory, objective.item), objective.count);
        case 'upgrade':
            return clamp(bestWornUpgrade(facts.inventory, objective.items) >= objective.level ? 1 : 0, 1);
        case 'wear':
            return clamp(wearsOneOf(facts.inventory, objective.items) ? 1 : 0, 1);
        case 'portal':
            return clamp((facts.portals ?? []).includes(objective.portal) ? 1 : 0, 1);
        case 'talk':
        case 'reach':
        case 'stand':
            return clamp(quest.flags.includes(index) ? 1 : 0, 1);
    }
}
/** Whether one of these taken quests asks to kill this mob. */
export function huntsMob(active, mobId) {
    for (const id of active.keys()) {
        const quest = getQuest(id);
        const asks = quest?.objectives.some((objective) => objective.kind === 'kill' && objective.mobs.some((mob) => mob === mobId));
        if (asks === true)
            return true;
    }
    return false;
}
export function questProgress(quest, active, facts) {
    return quest.objectives.map((objective, index) => objectiveProgress(objective, index, active, facts));
}
export function isQuestComplete(progress) {
    return progress.every((objective) => objective.value >= objective.target);
}
/** Why a quest may not be taken now, or null when it may. */
export function acceptRefusal(questId, facts) {
    const quest = getQuest(questId);
    if (quest === undefined)
        return 'unknown';
    if (facts.done.has(quest.id))
        return 'done';
    if (facts.active.has(quest.id))
        return 'active';
    if (!quest.requires.every((id) => facts.done.has(id)))
        return 'locked';
    if (facts.level < quest.level)
        return 'level';
    if (facts.active.size >= MAX_ACTIVE_QUESTS)
        return 'full';
    return null;
}
/** Quests this townsperson offers now; with `upcoming`, also those waiting only for a level. */
export function offersAt(npc, facts, upcoming = false) {
    return QUESTS.filter((quest) => {
        if (!isQuestNpc(quest.giver, npc))
            return false;
        const refusal = acceptRefusal(quest.id, facts);
        return refusal === null || refusal === 'full' || (upcoming && refusal === 'level');
    });
}
/** Taken quests this townsperson takes back, done or not. */
export function turnInsAt(npc, facts) {
    return [...facts.active.keys()].flatMap((id) => {
        const quest = getQuest(id);
        return quest !== undefined && isQuestNpc(quest.turnIn, npc) ? [quest] : [];
    });
}
/** The `talk` objectives this townsperson answers, not yet flagged. */
export function talksAt(npc, facts) {
    const talks = [];
    for (const [id, active] of facts.active) {
        const quest = getQuest(id);
        quest?.objectives.forEach((objective, index) => {
            if (objective.kind === 'talk' && isQuestNpc(objective.npc, npc)) {
                if (!active.flags.includes(index))
                    talks.push({ quest, index });
            }
        });
    }
    return talks;
}
/** The `reach` objectives this tile fulfils, not yet flagged. */
export function reachedAt(tile, places, facts) {
    const reached = [];
    for (const [id, active] of facts.active) {
        const quest = getQuest(id);
        quest?.objectives.forEach((objective, index) => {
            if (objective.kind !== 'reach' || active.flags.includes(index))
                return;
            const place = places.find((candidate) => candidate.name === objective.landmark);
            if (place === undefined)
                return;
            if (Math.hypot(tile.x - place.x, tile.y - place.y) <= place.radius) {
                reached.push({ quest, index });
            }
        });
    }
    return reached;
}
/** The `stand` objectives a fight won in this zone fulfils, not yet flagged (ADR 0068). */
export function standsIn(zone, facts, lookup = getQuest) {
    const stands = [];
    for (const [id, active] of facts.active) {
        const quest = lookup(id);
        quest?.objectives.forEach((objective, index) => {
            if (objective.kind === 'stand' && objective.zone === zone && !active.flags.includes(index)) {
                stands.push({ quest, index });
            }
        });
    }
    return stands;
}
/**
 * Whether a quest's stand is made: its `stand` objectives flagged while it
 * is under way, or the quest turned in. What the raid asks of each member.
 */
export function hasStood(questId, facts, lookup = getQuest) {
    if (facts.done.has(questId))
        return true;
    const active = facts.active.get(questId);
    const quest = lookup(questId);
    if (active === undefined || quest === undefined)
        return false;
    const stands = quest.objectives.flatMap((objective, index) => objective.kind === 'stand' ? [index] : []);
    return stands.length > 0 && stands.every((index) => active.flags.includes(index));
}
/**
 * What a quest's experience is worth at this level: like a kill, in full up
 * to two levels over the quest's, less beyond (`killXp`), so an old hand
 * does not farm the meadow's chain on a new character's heels.
 */
export function questXp(quest, playerLevel) {
    return killXp(quest.reward.xp, playerLevel, quest.level);
}
/** How close to a den of its creature a bait must be laid, diagonals included. */
export const BAIT_REACH = 6;
/**
 * Why a bait may not be laid here (ADR 0047), or null: a quest under way must
 * still ask for what it calls out, the player must stand by a den of its
 * `den` creature in that quest's region and out of any village, and nothing
 * called out by them may still be alive.
 */
export function baitRefusal(bait, facts, place) {
    const wanted = [...facts.active].some(([id, active]) => {
        const quest = getQuest(id);
        if (quest === undefined || quest.biome !== place.biome)
            return false;
        return quest.objectives.some((objective, index) => {
            if (objective.kind !== 'kill' || !objective.mobs.some((mob) => mob === bait.summons)) {
                return false;
            }
            const progress = objectiveProgress(objective, index, active, facts);
            return progress.value < progress.target;
        });
    });
    if (!wanted)
        return 'bait-not-now';
    const { tile } = place;
    const inVillage = place.villages.some((village) => Math.hypot(tile.x - village.x, tile.y - village.y) <= village.radius);
    const byDen = place.dens.some((den) => den.mob === bait.den &&
        Math.max(Math.abs(den.x - tile.x), Math.abs(den.y - tile.y)) <= BAIT_REACH);
    if (inVillage || !byDen)
        return 'bait-here';
    if (place.summoned)
        return 'bait-busy';
    return null;
}
/**
 * What the quests under way lent and still expect back (`grants`, a bait):
 * never destroyed while one of them runs, or the quest could not be done
 * (ADR 0084). Abandoned or turned in, the quest takes it back anyway.
 */
export function lentByQuests(active) {
    const lent = new Set();
    for (const id of active.keys()) {
        for (const grant of getQuest(id)?.grants ?? [])
            lent.add(grant.templateId);
    }
    return lent;
}
/** Whether a reward's item comes bound: equipment does, a potion or a material does not. */
export function questRewardBound(templateId) {
    return getItem(templateId)?.kind === 'equipment';
}
export function questMarker(npc, facts) {
    const back = turnInsAt(npc, facts);
    const ready = back.some((quest) => {
        const active = facts.active.get(quest.id);
        return active !== undefined && isQuestComplete(questProgress(quest, active, facts));
    });
    if (ready || talksAt(npc, facts).length > 0)
        return 'ready';
    if (offersAt(npc, facts).length > 0)
        return 'available';
    if (back.length > 0)
        return 'pending';
    return undefined;
}
//# sourceMappingURL=quests.js.map